import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Bell, Zap, Tag, Gift, ArrowRight } from 'lucide-react';
import { useAnnouncements } from '@/hooks/useAnnouncements';

/**
 * Floating banner that pops in when a new offer/announcement arrives.
 * Also shows a small "🔔 Enable alerts" prompt if browser notifications aren't granted yet.
 */
export default function AnnouncementBanner() {
  const { latest, markSeen, enablePushNotifications, pushStatus } = useAnnouncements();
  const [showPermPrompt, setShowPermPrompt] = useState(false);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    // Show the Enable prompt whenever push is supported and the user hasn't
    // subscribed yet (and hasn't previously denied). Independent of announcements —
    // visitors should be able to opt in BEFORE any deal drops.
    setShowPermPrompt(
      pushStatus.supported && !pushStatus.subscribed && pushStatus.permission !== 'denied'
    );
  }, [pushStatus.supported, pushStatus.subscribed, pushStatus.permission]);

  // Countdown remaining
  const remain = useCountdown(latest?.expires_at);

  if (!latest && !showPermPrompt) return null;

  const badgeColor = latest?.type === 'flash_sale' ? 'bg-red-500' : latest?.type === 'offer' ? 'bg-emerald-500' : 'bg-blue-500';
  const badgeIcon = latest?.type === 'flash_sale' ? Zap : latest?.type === 'offer' ? Tag : Bell;
  const BadgeIcon = badgeIcon;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-40 w-[min(92vw,560px)] pointer-events-none">
      {/* Enable-alerts prompt */}
      {showPermPrompt && (
        <div className="pointer-events-auto mb-2 flex items-center gap-3 bg-slate-900/95 border border-emerald-500/40 backdrop-blur-xl rounded-xl px-3 py-2 shadow-2xl" data-testid="notif-perm-prompt">
          <Bell className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <div className="text-white text-xs flex-1">
            Get notified when exhibitors post flash deals?
          </div>
          <button
            onClick={async () => {
              setEnabling(true);
              try {
                const res = await enablePushNotifications();
                if (res.subscribed || res.reason === 'denied') setShowPermPrompt(false);
              } finally {
                setEnabling(false);
              }
            }}
            disabled={enabling}
            className="px-2.5 py-1 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white text-[11px] font-bold disabled:opacity-60"
            data-testid="enable-notifications-btn"
          >
            {enabling ? 'Enabling…' : 'Enable'}
          </button>
          <button onClick={() => setShowPermPrompt(false)} className="p-1 text-slate-400 hover:text-white" data-testid="dismiss-perm-prompt">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Live announcement */}
      {latest && (
        <div
          className="pointer-events-auto rounded-2xl overflow-hidden shadow-2xl border border-emerald-400/30 bg-gradient-to-br from-slate-900/95 to-slate-950/95 backdrop-blur-xl animate-in slide-in-from-top-4 duration-300"
          data-testid="announcement-banner"
        >
          <div className="flex items-stretch">
            <div className={`${badgeColor} px-3 py-3 flex items-center justify-center min-w-[64px]`}>
              <BadgeIcon className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 p-3 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">
                    {latest.type === 'flash_sale' ? 'FLASH DEAL' : latest.type === 'offer' ? 'OFFER' : 'ANNOUNCEMENT'}
                    {latest.company?.name ? ` · ${latest.company.name}` : ''}
                  </div>
                  <div className="text-white font-black text-sm truncate mt-0.5">{latest.title}</div>
                  {latest.discount_label && (
                    <div className="mt-1 inline-block bg-yellow-400 text-slate-900 text-[11px] font-black px-2 py-0.5 rounded-full">
                      {latest.discount_label}
                    </div>
                  )}
                </div>
                <button onClick={() => markSeen(latest.id)} className="text-slate-400 hover:text-white p-1" data-testid="dismiss-announcement-btn">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex items-center gap-3 mt-2 text-[11px]">
                {remain && !remain.expired && (
                  <div className="text-red-300 font-bold flex items-center gap-1">
                    <Gift className="w-3 h-3" /> ⏱ {remain.mm}:{remain.ss} left
                  </div>
                )}
                <div className="flex-1" />
                {(latest.coupon_code || latest.booth?.id) && (
                  <Link
                    to={latest.coupon_code ? `/redeem/${latest.coupon_code}` : `/booth/${latest.booth.id}`}
                    onClick={() => markSeen(latest.id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white text-[11px] font-bold"
                    data-testid="announcement-cta"
                  >
                    View <ArrowRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!iso) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [iso]);
  if (!iso) return null;
  const diff = new Date(iso).getTime() - now;
  if (diff <= 0) return { mm: '00', ss: '00', expired: true };
  const mm = String(Math.floor(diff / 60000)).padStart(2, '0');
  const ss = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
  return { mm, ss, expired: false };
}
